// src/features/leases/components/LeaseTimeline.jsx
//
// LEASE-OVERVIEW-TIMELINE-SIMPLIFY — Clean term bar with 4 marker types
// and an explicit 4-rule collision system.
//
// Visual elements on the bar:
//   Elapsed segment   — solid fill (Commenced → Today)
//   Remaining segment — plain light-grey (Today → Expiry)
//   Today marker      — compact caption above the fill edge and a short cap
//   Renewal Notice    — amber dot at notice CD position (first upcoming renewal_notice)
//
// Marker labels occupy separate rows: Today above the bar, Renewal below it,
// and commencement/expiry dates on their own baseline.
//
// All dots/rings carry hover tooltips (exact date) as always-available fallback.
// Missing dates silently omit the marker.
//
// Removed: dashed future fill, amber renewal-window band, abatement ticks, Option 2 ring,
// five-item legend, all non-renewal-notice CD marker types.
//
// Kept unchanged: OngoingLeaseBar, TimelineFallback, RightEdgeTooltip, MarkerTooltip,
// resolveTimelineEndAnchor / parseDateOnly helpers, DecisionsAheadCallout integration,
// urgency mode (data-timeline-urgent) driven by the renewal notice alone.

import React from "react";
import { useHoverIntent } from "../../../components/useHoverIntent.js";
import { T } from "../../../app/tokens.js";
import { DecisionsAheadCallout } from "./DecisionsAheadCallout.jsx";
import { Tooltip } from "../../../components/Tooltip.jsx";
import {
  RED_THRESHOLD_DAYS,
  urgencyColorForMode,
  resolveTimelineEndAnchor,
  parseDateOnly,
} from "../leaseTabConfig.js";

// ─── Layout constants ─────────────────────────────────────────────────────────
const C_H         = 84;   // container height (px) — accommodates marker and date rows
const BAR_TOP     = 38;   // bar track top (px) — leaves room for Today label above
const BAR_H       = 6;    // bar track height (px)
const BAR_BOT     = BAR_TOP + BAR_H;  // 44
const RENEWAL_DOT_SIZE = 6;
const RENEWAL_ROW = BAR_BOT + 5;
const DATE_ROW    = 68;
const TODAY_CAP_H = 10;
const TODAY_CAP_TOP = BAR_H - TODAY_CAP_H;
const TODAY_CAPTION_TOP = TODAY_CAP_TOP - 12;

// ─── Collision thresholds ─────────────────────────────────────────────────────
const STAGGER_THRESHOLD  = 8;   // % — below this gap → stagger Notice to row 2

// ─── Colours ──────────────────────────────────────────────────────────────────
const NOTICE_COLOR = T.ragAmber;          // filled amber dot for renewal

// ─── Local helpers ─────────────────────────────────────────────────────────────

function fmtDate(d) {
  const dt = parseDateOnly(d);
  if (!dt) return null;
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function daysBetween(a, b) {
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

function fmtDayCount(n) {
  if (n == null) return null;
  return `${n.toLocaleString()}d`;
}

function pctOf(date, start, end) {
  const dt = parseDateOnly(date);
  if (!dt) return null;
  const total = end.getTime() - start.getTime();
  if (total <= 0) return null;
  const pos = ((dt.getTime() - start.getTime()) / total) * 100;
  return Math.max(0, Math.min(100, pos));
}

// ─── MarkerTooltip ────────────────────────────────────────────────────────────
//
// Wraps a marker element. Hover / keyboard focus reveals a tooltip with the
// marker's label + date. All markers receive this wrapper.
// Accessibility: role=button, tabIndex=0, Escape closes.

function MarkerTooltip({ id, color, label, date, days, children, style: wrapperStyle }) {
  const { open, triggerProps } = useHoverIntent();

  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-describedby={open ? `marker-tip-${id}` : undefined}
      {...triggerProps}
      style={{
        display: "block",
        cursor: "help",
        outline: "none",
        position: "relative",
        ...wrapperStyle,
      }}
    >
      {children}
      {open && (
        <Tooltip
          id={`marker-tip-${id}`}
          data-testid={`marker-tooltip-${id}`}
          style={{
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
          }}
        >
          <span style={{ display: "block", fontWeight: T.fwSemibold, color }}>{label}</span>
          {date && <span style={{ display: "block" }}>{date}</span>}
          {days != null && <span style={{ display: "block" }}>{fmtDayCount(days)}</span>}
        </Tooltip>
      )}
    </span>
  );
}

// ─── Fallback panel ────────────────────────────────────────────────────────────

function TimelineFallback({ message, decisions, decisionsLoading, hideDecisions = false }) {
  return (
    <section
      data-testid="lease-timeline"
      data-timeline-state="fallback"
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 6,
        padding: "11px 13px",
      }}
    >
      <div
        data-testid="lease-timeline-fallback"
        style={{
          fontSize: T.fsXs,
          color: T.light,
          fontStyle: "italic",
          padding: "12px 0",
          textAlign: "center",
        }}
      >
        {message}
      </div>
      {!hideDecisions && <DecisionsAheadCallout items={decisions} variant="cards" loading={decisionsLoading} />}
    </section>
  );
}

// ─── RightEdgeTooltip ─────────────────────────────────────────────────────────

function RightEdgeTooltip({ source, noticeDeadline, children }) {
  const { open, triggerProps } = useHoverIntent();
  const sourceLabel =
    source === "soonest_possible_expiration" ? "Soonest possible expiration"
    : source === "next_notice_deadline"      ? "Notice deadline"
    : source;

  return (
    <span
      role="button"
      tabIndex={0}
      aria-label="Projected end date"
      {...triggerProps}
      style={{ cursor: "help", outline: "none", position: "relative" }}
    >
      {children}
      {open && (
        <Tooltip
          data-testid="lease-timeline-right-edge-tooltip"
          style={{
            bottom: "calc(100% + 4px)",
            right: 0,
            zIndex: 20,
          }}
        >
          {`Projected end - sourced from ${sourceLabel}`}
          {noticeDeadline && (
            <span style={{ display: "block", marginTop: 2 }}>
              {`next notice deadline ${noticeDeadline}`}
            </span>
          )}
        </Tooltip>
      )}
    </span>
  );
}

// ─── OngoingLeaseBar ──────────────────────────────────────────────────────────

function OngoingLeaseBar({ startDate, decisions, decisionsLoading, hideDecisions = false }) {
  const DASHED_GRAY = T.provenanceComputed;
  const BAR_BG = T.kpiTileBg;
  return (
    <section
      data-testid="lease-timeline"
      data-timeline-state="ongoing"
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 6,
        padding: "11px 13px",
      }}
    >
      <div style={{ position: "relative", height: 38 }}>
        <div style={{
          position: "absolute", top: 10, left: 0, right: 0, height: 6,
          background: BAR_BG, borderRadius: 3,
        }} />
        <div
          data-testid="lease-timeline-ongoing-bar"
          style={{
            position: "absolute", top: 10, left: 0, right: 0, height: 6,
            background:
              "linear-gradient(to right, rgba(55,138,221,1) 0%, rgba(55,138,221,0.3) 100%)",
            borderRadius: 3,
          }}
        />
        <div style={{
          position: "absolute", top: 6, left: 0, width: 2, height: 14,
          background: T.timelineBlue, borderRadius: 1,
        }} />
        <div style={{
          position: "absolute", top: 3, right: 0,
          fontSize: T.fsXxxs, color: DASHED_GRAY, fontStyle: "italic", lineHeight: "19px",
        }}>
          Ongoing
        </div>
      </div>
      <div style={{
        display: "flex", justifyContent: "space-between",
        fontSize: T.fsXxs, color: T.light,
        marginTop: 4, marginBottom: 10,
      }}>
        <span data-testid="lease-timeline-start-label">{fmtDate(startDate)}</span>
        <span data-testid="lease-timeline-ongoing-label" style={{ fontStyle: "italic" }}>
          Ongoing
        </span>
      </div>
      {!hideDecisions && <DecisionsAheadCallout items={decisions} variant="cards" loading={decisionsLoading} />}
    </section>
  );
}

// ─── LeaseTimeline ────────────────────────────────────────────────────────────

export function LeaseTimeline({
  startDate,
  endDate,
  soonestPossibleExpiration = null,
  nextNoticeDeadline = null,
  today = new Date(),
  criticalDates = [],
  decisions = [],
  decisionsLoading = false,
  hideDecisions = false,
  // abatementRows and option2Date are accepted but ignored (removed features).
  abatementRows: _abatementRows,
  option2Date: _option2Date,
}) {
  const start = parseDateOnly(startDate);

  if (!start) {
    return <TimelineFallback message="No commencement date set" decisions={decisions} decisionsLoading={decisionsLoading} hideDecisions={hideDecisions} />;
  }

  const anchor = resolveTimelineEndAnchor({
    endDate,
    soonestPossibleExpiration,
    nextNoticeDeadline,
  });

  if (anchor.mode === "ongoing" && !endDate) {
    return <TimelineFallback message="No end date set" decisions={decisions} decisionsLoading={decisionsLoading} hideDecisions={hideDecisions} />;
  }

  if (anchor.mode === "ongoing") {
    return (
      <OngoingLeaseBar
        startDate={startDate}
        decisions={decisions}
        decisionsLoading={decisionsLoading}
        hideDecisions={hideDecisions}
      />
    );
  }

  const end = parseDateOnly(anchor.date);

  if (!end) {
    return <TimelineFallback message="No end date set" decisions={decisions} decisionsLoading={decisionsLoading} hideDecisions={hideDecisions} />;
  }
  if (end.getTime() <= start.getTime()) {
    return <TimelineFallback message="End date is before commencement" decisions={decisions} decisionsLoading={decisionsLoading} hideDecisions={hideDecisions} />;
  }

  const isProjected =
    anchor.mode === "projected_expiration" || anchor.mode === "projected_notice";
  const projectedSubLabel =
    anchor.mode === "projected_expiration" ? "(earliest exit)" : "(notice deadline)";

  // ── Today position ───────────────────────────────────────────────────────────
  const todayInWindow =
    today.getTime() >= start.getTime() && today.getTime() <= end.getTime();
  const todayPct = todayInWindow ? pctOf(today, start, end) : null;

  // ── Urgency (driven by renewal notice alone) ─────────────────────────────────
  const modeUrgencyColor = urgencyColorForMode(anchor.mode);

  // ── Renewal notice — first upcoming renewal_notice CD within window ────────────
  const renewalNoticeCD = [...(criticalDates || [])]
    .filter((cd) => {
      if (cd.type !== "renewal_notice") return false;
      const dt = parseDateOnly(cd.dueDate);
      return dt && dt.getTime() > today.getTime() && dt.getTime() < end.getTime();
    })
    .sort((a, b) =>
      parseDateOnly(a.dueDate).getTime() - parseDateOnly(b.dueDate).getTime()
    )[0] || null;

  const rawNoticePct = renewalNoticeCD ? pctOf(renewalNoticeCD.dueDate, start, end) : null;
  const noticePct    = rawNoticePct != null && rawNoticePct < 99.5 ? rawNoticePct : null;

  const noticeDaysFromToday = renewalNoticeCD && noticePct != null
    ? daysBetween(today, parseDateOnly(renewalNoticeCD.dueDate))
    : null;
  const noticeUrgent = noticeDaysFromToday != null
    && noticeDaysFromToday > 0
    && noticeDaysFromToday < RED_THRESHOLD_DAYS;

  const hasUrgentMarker = noticeUrgent;
  const urgencyColor    = hasUrgentMarker ? modeUrgencyColor : null;
  const PROGRESS_COLOR  = urgencyColor || T.timelineBlue;
  const BAR_BG          = T.kpiTileBg;

  // Preserve the proximity indicator while keeping marker labels on separate rows.
  // expiryPct is always 100 (notice pct is measured on the same [start, end] axis).
  const EXPIRY_PCT = 100;
  const gap = noticePct != null ? EXPIRY_PCT - noticePct : Infinity;

  const anyCollision = gap < STAGGER_THRESHOLD;

  // ── Projected-mode right-edge tooltip notice deadline label ──────────────────
  const projectedNoticeLabel = anchor.mode === "projected_notice"
    ? parseDateOnly(anchor.date)?.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  // ── Runway header stats ───────────────────────────────────────────────────────
  const yrsLeft = todayPct != null
    ? Math.max(0, (end.getTime() - today.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : null;
  const elapsedPct = todayPct != null ? Math.round(todayPct) : null;

  return (
    <section
      data-testid="lease-timeline"
      data-timeline-state="ok"
      data-timeline-urgent={hasUrgentMarker || undefined}
      data-timeline-stagger={anyCollision || undefined}
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 6,
        padding: "11px 13px",
      }}
    >
      {/* ── Runway header ─────────────────────────────────────────────────── */}
      <div
        data-testid="lease-timeline-runway-header"
        style={{
          display:      "flex",
          alignItems:   "center",
          gap:          6,
          marginBottom: 8,
          flexWrap:     "wrap",
        }}
      >
        <span style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>
          Term runway
        </span>
        {yrsLeft != null && (
          <>
            <span style={{ color: T.border }}>·</span>
            <span
              data-testid="lease-timeline-yrs-left"
              style={{ fontSize: T.fsXs, color: T.mid }}
            >
              ~{yrsLeft.toFixed(1)} yrs left
            </span>
            <span style={{ color: T.border }}>·</span>
            <span
              data-testid="lease-timeline-elapsed-pct"
              style={{ fontSize: T.fsXs, color: T.mid }}
            >
              {elapsedPct}% elapsed
            </span>
          </>
        )}
      </div>

      {/* ── Bar track with all positioned elements ──────────────────────────── */}
      <div style={{ position: "relative", height: C_H }}>

        {/* Track, elapsed fill, and Today cap share one bar-contained coordinate system. */}
        <div
          data-testid="lease-timeline-track"
          style={{
            position: "absolute",
            top: BAR_TOP,
            left: 0,
            right: 0,
            height: BAR_H,
            background: BAR_BG,
            borderRadius: 3,
          }}
        >
          {todayPct != null && (
            <div
              data-testid="lease-timeline-progress"
              data-urgent={hasUrgentMarker || undefined}
              data-urgency-color={urgencyColor || undefined}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: `${todayPct}%`,
                height: "100%",
                background: PROGRESS_COLOR,
                borderRadius: "3px 0 0 3px",
              }}
            />
          )}
          {todayPct != null && (
            <div
              data-testid="lease-timeline-marker-today"
              data-urgent={hasUrgentMarker || undefined}
              data-edge-align={todayPct >= 80 ? "right" : todayPct <= 12 ? "left" : "center"}
              style={{
                position: "absolute",
                left: `${todayPct}%`,
                top: TODAY_CAP_TOP,
                width: 3,
                height: TODAY_CAP_H,
                transform: todayPct >= 100 ? "translateX(-100%)" : "translateX(-50%)",
                zIndex: 2,
              }}
            >
              <MarkerTooltip
                id="today"
                color={T.blue}
                label="Today"
                date={fmtDate(today)}
                style={{ width: "100%", height: "100%" }}
              >
                <div
                  data-testid="lease-timeline-today-cap"
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: T.blue,
                    borderRadius: 2,
                  }}
                />
              </MarkerTooltip>
            </div>
          )}
        </div>

        {/* Today caption sits just above the short cap and clamps near the end. */}
        {todayPct != null && (
          <div
            data-testid="lease-timeline-today-caption-row"
            style={{ position: "absolute", top: BAR_TOP + TODAY_CAPTION_TOP, left: 0, right: 0, height: 13 }}
          >
            <span
              data-testid="lease-timeline-today-caption"
              data-edge-align={todayPct >= 80 ? "right" : todayPct <= 12 ? "left" : "center"}
              style={{
                position: "absolute",
                left: `${todayPct}%`,
                top: 0,
                transform: todayPct >= 80
                  ? "translateX(-100%)"
                  : todayPct <= 12 ? undefined : "translateX(-50%)",
                fontSize: T.fsXxxs5,
                fontWeight: T.fwSemibold,
                letterSpacing: ".03em",
                color: T.ragGreenText,
                lineHeight: "12px",
                whiteSpace: "nowrap",
              }}
            >
              Today
            </span>
          </div>
        )}

        {/* Renewal Notice dot — amber, centered on the bar at noticePct */}
        {noticePct != null && renewalNoticeCD && (
          <div
            data-testid={`lease-timeline-marker-cd-${renewalNoticeCD.id}`}
            data-urgent={noticeUrgent || undefined}
            style={{
              position: "absolute", top: BAR_TOP + (BAR_H - RENEWAL_DOT_SIZE) / 2,
              left: `${noticePct}%`, transform: "translateX(-50%)",
            }}
          >
            <MarkerTooltip
              id={`cd-${renewalNoticeCD.id}`}
              color={NOTICE_COLOR}
              label="Renewal"
              date={fmtDate(renewalNoticeCD.dueDate)}
              days={noticeDaysFromToday}
            >
              <div
                data-testid={`lease-timeline-cd-line-${renewalNoticeCD.id}`}
                style={{
                  width: RENEWAL_DOT_SIZE,
                  height: RENEWAL_DOT_SIZE,
                  borderRadius: "50%",
                  background: NOTICE_COLOR,
                }}
              />
            </MarkerTooltip>
          </div>
        )}

        {/* ── Renewal label row and separate endpoint-date baseline ─────── */}
        {/* Wrapper div with color: T.light — gives parentElement T.light for tests */}
        <div style={{ color: T.light }}>

          {/* Commenced label — left-anchored on the endpoint-date baseline */}
          <span
            data-testid="lease-timeline-start-label"
            style={{
              position: "absolute", left: 0, top: DATE_ROW,
              fontSize: T.fsXxs, whiteSpace: "nowrap",
            }}
          >
            {fmtDate(startDate)}
          </span>

          {/* Renewal label — amber, directly below the track and edge-clamped */}
          {noticePct != null && renewalNoticeCD && (
            <span
              data-testid="lease-timeline-renewal-label"
              data-edge-align={noticePct <= 12 ? "left" : noticePct >= 88 ? "right" : "center"}
              style={{
              position: "absolute",
              left: `${noticePct}%`,
              top: RENEWAL_ROW,
              transform: noticePct <= 12 ? "none" : noticePct >= 88 ? "translateX(-100%)" : "translateX(-50%)",
              fontSize: T.fsXxs,
              whiteSpace: "nowrap",
              textAlign: "center",
              color: NOTICE_COLOR,
              fontWeight: T.fwSemibold,
            }}>
              Renewal
            </span>
          )}

          {/* Expiry label — right-anchored on the endpoint-date baseline */}
          {isProjected ? (
            <RightEdgeTooltip source={anchor.source} noticeDeadline={projectedNoticeLabel}>
              <span
                data-testid="lease-timeline-end-label"
                style={{
                  position: "absolute", right: 0, top: DATE_ROW,
                  fontSize: T.fsXxs, textAlign: "right",
                }}
              >
                {fmtDate(anchor.date)}
                <span style={{
                  display: "block", fontStyle: "italic",
                  fontSize: T.fsXxxs, color: T.provenanceComputed,
                }}>
                  {projectedSubLabel}
                </span>
              </span>
            </RightEdgeTooltip>
          ) : (
            <span
              data-testid="lease-timeline-end-label"
              style={{
                position: "absolute", right: 0, top: DATE_ROW,
                fontSize: T.fsXxs, textAlign: "right",
              }}
            >
              {fmtDate(anchor.date)}
            </span>
          )}
        </div>
      </div>

      {/* ── Decisions ahead — cards variant ────────────────────────────────── */}
      {!hideDecisions && <DecisionsAheadCallout items={decisions} variant="cards" loading={decisionsLoading} />}
    </section>
  );
}
